import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import i18next from 'i18next';
import { fetchOrcaProxy, get, getData } from '../services/api';
import {
  getAllBrandsData as GET_ALL_BRANDS_DATA,
  getAllBrandsDataFailed,
  getAllBrandsDataLoaded,
  updatePaymentMethodCaSync as UPDATE_PAYMENT_METHOD_CA_SYNC,
  convertMonerisTicket as CONVERT_MONERIS_TICKET,
  getMonerisTicket as GET_MONERIS_TICKET,
  loadBillingInfoAllBrand as LOAD_BILLING_INFO_ALL_BRAND,
  monerisTicketFailed,
  monerisTicketLoaded,
  monerisTicketReset,
  updatePaymentMethodCa as UPDATE_PAYMENT_METHOD_CA,
  updatePaymentMethodCaError,
  updatePaymentMethodCaSync,
  getOrcaCustomerValues as GET_ORCA_CUSTOMER_VALUES,
  getOrcaCustomerValuesLoaded,
  updatePaymentMethodCaCompleted,
  policyDetailsLoaded,
  billingInfoLoaded,
} from '../actions';
import { WEBVIEW_API_TOKEN } from '../services/constants';
import { PAYMENT_PROVIDERS_ID } from '../components/myAccount/BillingAndPayments/billingAndPayments.constants';
import { doFetch } from '../services/utils';
import { usePetCloudPolicySummaries } from '../services/featureFlagsForSagas';

export function* getAllBrandsData() {
  // remove after orca PM release
  const usePolicySummaries = usePetCloudPolicySummaries();

  const service = usePolicySummaries ? 'policy' : 'api';
  const url = `${service}/MultiBrand/Customer/PolicySummaries`;

  const response = yield call(get, null, url);

  if (response.IsValid && response.Data) {
    yield put(getAllBrandsDataLoaded(response.Data));
  } else {
    yield put(getAllBrandsDataFailed());
  }
}

export function* getOrcaCustomerValues(dispatch, { payload }) {
  const { PolicyId } = payload;
  const url = `policy/MultiBrand/Customer/PaymentManagement/${PolicyId}`;

  const response = yield call(get, dispatch, url);

  if (response.success) {
    yield put(getOrcaCustomerValuesLoaded(response));
  }
}

function* syncPaymentMethod(payload) {
  const {
    baseUrl = 'https://api.ca-stg.petpartners.com',
    customerId,
    customerPaymentOptionId,
    policyId,
  } = payload;

  try {
    const token = yield call(getData, WEBVIEW_API_TOKEN);

    const path = `orca-proxy/billing/v1/policy/${policyId}/`
      + `payment-option/${customerPaymentOptionId}?base-url=${baseUrl}`;
    const options = {
      headers: {
        authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    };

    const request = yield call(fetchOrcaProxy, path, options);

    if (request.ok) {
      const prefixId = process.env.REACT_APP_NAME === 'prd'
        ? 'azure-ca' : 'azure-ca-stg';

      const originId = 'PPUPC';
      const pathSyn = 'tst/sync/OrcaAccountSync/'
        + `${originId}/${prefixId}_${customerId}`;
      options.headers.authorization =
        `APIKEY ${process.env.REACT_APP_ORCA_PROXY_API_KEY}`;

      const requestSync = yield call(fetchOrcaProxy, pathSyn, options);

      if (requestSync.ok) {
        yield put(updatePaymentMethodCaSync());
      } else {
        yield put(updatePaymentMethodCaError(
          `Sync failed with status ${requestSync.status}`,
        ));
      }
    } else {
      yield put(updatePaymentMethodCaError(
        `Update policy payment option failed with status ${request.status}`,
      ));
    }
  } catch (error) {
    yield put(updatePaymentMethodCaError(error.message));
  }
}

/**
 * Use it for syncing the payment method.
 */
export function* updatePaymentMethodCa({ payload }) {
  const {
    baseUrl = 'https://api.ca-stg.petpartners.com',
    customerId,
    paymentMethodId,
    policyId,
    paymentProviderId = PAYMENT_PROVIDERS_ID.MONERIS,
  } = payload;

  try {
    const token = yield call(getData, WEBVIEW_API_TOKEN);
    const store = yield select(({ personalInformation }) => ({
      personalInformation,
    }));
    const { userInfo } = store.personalInformation;

    const path = 'orca-proxy/billing/v1/'
      + `payment-option?base-url=${baseUrl}`;

    const CREDIT_CARD_PM = 2;

    const body = {
      address1: userInfo.Address?.Line1,
      address2: userInfo.Address?.Line2,
      city: userInfo.Address?.City,
      customerId,
      editing: false,
      firstName: userInfo.FirstName,
      gatewayToken: paymentMethodId,
      lastName: userInfo.LastName,
      paymentProviderId,
      paymentTypeId: CREDIT_CARD_PM,
      postalCode: userInfo.Address?.ZipCode,
      stateProvCode: userInfo.Address?.StateId,
    };

    const options = {
      body: JSON.stringify(body),
      headers: {
        authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    };

    const request = yield call(fetchOrcaProxy, path, options);
    if (request.ok) {
      const response = yield request.json();
      yield call(syncPaymentMethod, {
        baseUrl,
        customerId,
        customerPaymentOptionId: response.customerPaymentOptionId,
        policyId,
      });
    } else {
      yield put(updatePaymentMethodCaError(
        `Save payment option failed with status ${request.status}`,
      ));
    }
  } catch (error) {
    yield put(updatePaymentMethodCaError(error.message));
  } finally {
    yield put(updatePaymentMethodCaCompleted());
  }
}

export function* getMonerisTicket({ payload }) {
  try {
    const { baseUrl } = payload;
    const language = i18next.language.split('-')[0];

    const path = 'billing/v1/moneris/ticket/'
      + `transaction-total/0?localizationCode=${language}`;
    const url = `${baseUrl}/${path}`;

    const request = yield call(fetch, url);
    const response = yield request.json();

    if (request.ok) {
      yield put(monerisTicketLoaded(response.ticket));
    } else {
      yield put(monerisTicketFailed(
        `Get ticket failed with status ${request.status}`,
      ));
    }
  } catch (error) {
    yield put(monerisTicketFailed(error.message));
  }
}

export function* convertTicketToPaymentId({ payload }) {
  try {
    const {
      baseUrl,
      customerId,
      policyId,
      ticket,
    } = payload;

    const url = `${baseUrl}/billing/v1/moneris/convert-ticket`;
    const body = { ticket };

    const params = { body, method: 'POST', url };
    const request = yield call(doFetch, params);
    const response = yield request.json();

    if (request.ok) {
      const data = {
        baseUrl,
        customerId,
        paymentMethodId: response.paymentMethodId,
        paymentProviderId: PAYMENT_PROVIDERS_ID.MONERIS,
        policyId,
      };

      yield call(updatePaymentMethodCa, { payload: data });
    } else {
      yield put(updatePaymentMethodCaError(
        `Convert ticket failed with status ${request.status}`,
      ));
    }
  } catch (error) {
    yield put(monerisTicketReset());
    yield put(updatePaymentMethodCaError(error.message));
  }
}

export function* loadBillingInfoAllBrand() {
  yield call(getAllBrandsData);
  yield put(policyDetailsLoaded());
  yield put(billingInfoLoaded());
}

export function* billingSaga(store) {
  yield all([
    takeLatest(GET_ALL_BRANDS_DATA, getAllBrandsData, store.dispatch),
    takeLatest(UPDATE_PAYMENT_METHOD_CA_SYNC, getAllBrandsData, store.dispatch),
    takeLatest(UPDATE_PAYMENT_METHOD_CA, updatePaymentMethodCa),
    takeLatest(GET_MONERIS_TICKET, getMonerisTicket),
    takeLatest(
      CONVERT_MONERIS_TICKET,
      convertTicketToPaymentId,
      store.dispatch,
    ),
    takeLatest(GET_ORCA_CUSTOMER_VALUES, getOrcaCustomerValues, store.dispatch),
    takeLatest(LOAD_BILLING_INFO_ALL_BRAND, loadBillingInfoAllBrand),
  ]);
}
